import type { GeotabModules } from "../../../native-sdk";
import { CancelablePromise } from "../cancelablePromise";
import * as logger from "../logger";
import { AppType, getAppType } from "./nativeHelpers";
import { AddinFamily, getAddinFolder, getListedFamily, getListedUrls, isAddinFamily } from "./addinNavigationItemList";

/**
 * api.mobile.bottomNavigationItem (MYG-154446): an add-in on the curated list puts one item in the native bottom
 * bar, in the place of one of its buttons (Messages unless the add-in names another), and takes it away again.
 *
 * This module is the Drive side of the request. It decides whether the caller may make it and whether the item is
 * acceptable, and it hands native only what passed. Native decides who owns the item (the first add-in to place
 * one) and draws it. Every failure resolves `false` and nothing here rejects, so an add-in written for a host
 * without the feature keeps working when it runs on one.
 */

/** How long to wait for native before telling the add-in the item was not applied. */
const NATIVE_TIMEOUT_MS = 2000;
export const MAX_TEXT_LENGTH = 20;
export const MAX_ICON_URL_LENGTH = 2048;

/** The buttons of the native bottom bar that an item can take the place of. */
export const REPLACEABLE_BUTTONS = ["dashboard", "hos", "inspections", "messages", "trips", "more"] as const;
export type ReplaceableButton = typeof REPLACEABLE_BUTTONS[number];

const DEFAULT_REPLACED_BUTTON: ReplaceableButton = "messages";

function isReplaceableButton(value: unknown): value is ReplaceableButton {
    return typeof value === "string" && (REPLACEABLE_BUTTONS as readonly string[]).indexOf(value) >= 0;
}

/** What an add-in may ask for. Fields it adds that are not listed here are ignored. */
export interface IBottomNavigationItem {
    /** Plain text, 1 to 20 Unicode code points counted after trimming and NFC normalisation, shown as it is. Drive does not translate it: pick the language yourself. */
    text: string;
    /** An https image (svg or png) in the folder of your own add-in file. Without one, a built-in icon shows. */
    iconUrl?: string;
    /** What a tap does. The only action is to open an installed add-in of a listed product. */
    action: { openAddin: AddinFamily };
    /** Give up the item as the tap opens the add-in, so the add-in that opens can place its own. */
    releaseOnPress?: boolean;
    /**
     * The button of the bar the item takes the place of. Default "messages". The call is refused when the driver
     * has no such button (no access to HOS, Inspections or Messages, or Trips does not fit in the bar).
     */
    replaces?: ReplaceableButton;
}

/** An installed add-in, as the database configures it. */
export interface IInstalledAddin {
    name: string;
    url: string;
}

interface IValidItem {
    text: string;
    iconUrl: string | null;
    family: AddinFamily;
    releaseOnPress: boolean;
    replaces: ReplaceableButton;
}

type Validation = { item: IValidItem } | { reason: string };

/** True on a DriveNative build that ships the item bridge. api.mobile.bottomNavigationItem exists only then. */
export function isAddinNavigationItemSupported(): boolean {
    const module = window.geotabModules?.addinBottomBar;
    return getAppType() === AppType.NativeSdk && typeof module?.setItem === "function" && typeof module?.clearItem === "function";
}

/** Code points that draw nothing, draw as blank, change the direction of the text, or are not text at all (inclusive ranges). */
const UNACCEPTABLE_RANGES: readonly (readonly [number, number])[] = [
    [0x0, 0x1f], [0x7f, 0x9f], [0xad, 0xad], [0x34f, 0x34f], [0x61c, 0x61c], [0x115f, 0x1160], [0x17b4, 0x17b5], [0x180e, 0x180e],
    [0x200b, 0x200b], [0x200e, 0x200f], [0x2028, 0x202e], [0x2060, 0x206f], [0x2800, 0x2800], [0x3164, 0x3164],
    [0xd800, 0xf8ff], [0xfeff, 0xfeff], [0xffa0, 0xffa0], [0xfff0, 0xffff], [0x1bca0, 0x1bca3], [0x1d173, 0x1d17a],
    [0xe0000, 0x10ffff]
];

/**
 * Joiners, variation selectors and combining marks are needed inside text (Persian, Indic scripts, Mongolian, emoji,
 * accents) but draw nothing without a character before them. Whitespace is not visible either (inclusive ranges).
 * These ranges are the fallback for an engine without Unicode property escapes, which matches every mark by category.
 */
const DRAWS_NOTHING_ALONE_RANGES: readonly (readonly [number, number])[] = [
    [0x300, 0x36f], [0x180b, 0x180d], [0x180f, 0x180f], [0x1ab0, 0x1aff], [0x1dc0, 0x1dff], [0x200c, 0x200d],
    [0x20d0, 0x20ff], [0xfe00, 0xfe0f], [0xfe20, 0xfe2f]
];

function isUnacceptableCodePoint(codePoint: number): boolean {
    return UNACCEPTABLE_RANGES.some(([first, last]) => codePoint >= first && codePoint <= last);
}

// Built from a string so that a parser without Unicode property escapes does not fail on the file.
const COMBINING_MARK: RegExp | null = (() => {
    try {
        return new RegExp("^\\p{M}$", "u");
    } catch {
        return null;
    }
})();

function drawsNothingAlone(character: string, codePoint: number): boolean {
    return /\s/.test(character)
        || (COMBINING_MARK !== null && COMBINING_MARK.test(character))
        || DRAWS_NOTHING_ALONE_RANGES.some(([first, last]) => codePoint >= first && codePoint <= last);
}

function describeError(error: unknown): string {
    try {
        return error instanceof Error ? error.message : String(error);
    } catch {
        return "unknown error";
    }
}

function validateText(value: unknown): string | { reason: string } {
    if (typeof value !== "string") {
        return { reason: "text is not a string" };
    }
    const text = (typeof value.normalize === "function" ? value.normalize("NFC") : value).trim();
    const characters = Array.from(text);
    if (characters.length === 0) {
        return { reason: "text is empty" };
    }
    if (characters.length > MAX_TEXT_LENGTH) {
        return { reason: `text is longer than ${ MAX_TEXT_LENGTH } code points` };
    }
    let drawsSomething = false;
    for (const character of characters) {
        const codePoint = character.codePointAt(0) as number;
        if (character === "<" || character === ">" || isUnacceptableCodePoint(codePoint)) {
            return { reason: "text has a character that is not allowed" };
        }
        drawsSomething = drawsSomething || !drawsNothingAlone(character, codePoint);
    }
    if (!drawsSomething) {
        return { reason: "text has nothing visible" };
    }
    return text;
}

function validateIconUrl(value: unknown, callerUrl: string): string | null | { reason: string } {
    if (value === undefined || value === null) {
        return null;
    }
    if (typeof value !== "string" || value.length === 0 || value.length > MAX_ICON_URL_LENGTH) {
        return { reason: "iconUrl is not a usable string" };
    }
    let parsed: URL;
    try {
        parsed = new URL(value);
    } catch {
        return { reason: "iconUrl is not a URL" };
    }
    if (parsed.username !== "" || parsed.password !== "") {
        return { reason: "iconUrl must not carry a user name or a password" };
    }
    if (!/\.(svg|png)$/i.test(parsed.pathname)) {
        return { reason: "iconUrl must point to an svg or png file" };
    }
    // The folder is compared on the URL as the URL parser normalises it, so a dot segment or an encoded slash cannot
    // leave it. The origin is the add-in's, which is https, so only an https icon matches.
    const folder = new URL(getAddinFolder(callerUrl));
    if (parsed.origin !== folder.origin || !parsed.pathname.startsWith(folder.pathname) || /%2f|%5c/i.test(parsed.pathname)) {
        return { reason: "iconUrl is not in the folder of the add-in" };
    }
    // The URL parser percent-encodes non-ASCII characters, so the length that native receives can exceed the length checked above.
    if (parsed.href.length > MAX_ICON_URL_LENGTH) {
        return { reason: "iconUrl is too long once encoded" };
    }
    return parsed.href;
}

function validateItem(raw: unknown, callerUrl: string): Validation {
    if (typeof raw !== "object" || raw === null || Array.isArray(raw)) {
        return { reason: "item is not an object" };
    }
    // Each property is read once: an accessor could answer one value to the check and another to the result.
    const { text: rawText, iconUrl: rawIconUrl, action, releaseOnPress, replaces } = raw as Record<string, unknown>;
    const text = validateText(rawText);
    if (typeof text !== "string") {
        return text;
    }
    const iconUrl = validateIconUrl(rawIconUrl, callerUrl);
    if (iconUrl !== null && typeof iconUrl !== "string") {
        return iconUrl;
    }
    const family = typeof action === "object" && action !== null ? (action as Record<string, unknown>).openAddin : undefined;
    if (!isAddinFamily(family)) {
        return { reason: "action must be { openAddin: \"goroute\" | \"navigation\" }" };
    }
    if (releaseOnPress !== undefined && typeof releaseOnPress !== "boolean") {
        return { reason: "releaseOnPress is not a boolean" };
    }
    if (replaces !== undefined && !isReplaceableButton(replaces)) {
        return { reason: `replaces must be one of ${ REPLACEABLE_BUTTONS.join(", ") }` };
    }
    return {
        item: {
            text,
            iconUrl,
            family,
            releaseOnPress: releaseOnPress === true,
            replaces: replaces ?? DEFAULT_REPLACED_BUTTON
        }
    };
}

/**
 * The installed add-in called `name`, or null when there is none or more than one. Drive opens an add-in by name and
 * takes the first match, so a name that two add-ins share does not identify one of them.
 */
export function findUniquelyNamedAddin(installed: readonly IInstalledAddin[], name: string): IInstalledAddin | null {
    const matches = installed.filter(addin => addin.name === name);
    return matches.length === 1 ? matches[0] : null;
}

type Target = { addin: IInstalledAddin } | { reason: string };

const SHARED_NAME = "name is shared with another installed add-in";

/** The installed add-in a tap should open: the first of the family, in the order the database lists them, if its name is unique. */
function findTarget(family: AddinFamily, installed: readonly IInstalledAddin[]): Target {
    const urls = getListedUrls(family);
    const first = installed.find(addin => urls.indexOf(addin.url) >= 0);
    if (first === undefined) {
        return { reason: `no ${ family } add-in is installed` };
    }
    return findUniquelyNamedAddin(installed, first.name) === first
        ? { addin: first }
        : { reason: `the ${ family } add-in's ${ SHARED_NAME }` };
}

type Method = "setItem" | "clearItem";
type NativeCallback = (error?: Error, applied?: boolean) => void;

function callNative(method: Method, invoke: (module: NonNullable<GeotabModules["addinBottomBar"]>, callback: NativeCallback) => void): CancelablePromise<boolean> {
    return new CancelablePromise<boolean>(resolve => {
        let settled = false;
        const settle = (applied: boolean) => {
            if (settled) {
                return;
            }
            settled = true;
            resolve(applied);
        };
        const timer = window.setTimeout(() => {
            logger.log(logger.Source.NATIVE, `addinNavigationItem ${ method }: native did not respond`);
            settle(false);
        }, NATIVE_TIMEOUT_MS);
        try {
            const module = window.geotabModules?.addinBottomBar;
            if (module === undefined) {
                throw new Error("addinBottomBar is missing");
            }
            invoke(module, (error?: Error, applied?: boolean) => {
                window.clearTimeout(timer);
                if (error) {
                    logger.error(logger.Source.NATIVE, `addinNavigationItem ${ method }: ${ error.message }`);
                    settle(false);
                    return;
                }
                settle(applied === true);
            });
        } catch (error) {
            window.clearTimeout(timer);
            logger.error(logger.Source.NATIVE, `addinNavigationItem ${ method } threw: ${ describeError(error) }`);
            settle(false);
        }
    });
}

function refuse(owner: string, reason: string): CancelablePromise<boolean> {
    logger.log(logger.Source.ADDIN, `bottomNavigationItem refused for ${ owner }: ${ reason }`);
    return CancelablePromise.resolve(false);
}

/** Place or replace the item of `owner`, an add-in whose file is `callerUrl`. Never rejects; the reasons for `false` are in `IAddinApi`. */
export function setAddinNavigationItem(
    owner: string,
    callerUrl: string | null,
    raw: unknown,
    installed: readonly IInstalledAddin[]
): CancelablePromise<boolean> {
    if (!isAddinNavigationItemSupported()) {
        return CancelablePromise.resolve(false);
    }
    if (callerUrl === null) {
        const shared = installed.filter(addin => addin.name === owner).length > 1;
        return refuse(owner, shared ? `the add-in's ${ SHARED_NAME }` : "the add-in is not installed");
    }
    if (getListedFamily(callerUrl) === null) {
        return refuse(owner, "the add-in is not on the list");
    }
    let validation: Validation;
    let target: Target;
    try {
        validation = validateItem(raw, callerUrl);
        if ("reason" in validation) {
            return refuse(owner, validation.reason);
        }
        target = findTarget(validation.item.family, installed);
    } catch (error) {
        return refuse(owner, `the item could not be read: ${ describeError(error) }`);
    }
    if ("reason" in target) {
        return refuse(owner, target.reason);
    }
    const { text, iconUrl, releaseOnPress, replaces } = validation.item;
    const argument = { owner, text, iconUrl, target: target.addin.name, releaseOnPress, replaces };
    return callNative("setItem", (module, callback) => {
        if (module.setItem === undefined) {
            throw new Error("setItem is missing");
        }
        module.setItem(argument, callback);
    });
}

/**
 * Take the item of `owner` away. Native only clears an item its owner holds, so this does not check the add-in list:
 * an add-in that was removed or unlisted after it placed the item can still give it up. Never rejects.
 */
export function clearAddinNavigationItem(owner: string): CancelablePromise<boolean> {
    if (!isAddinNavigationItemSupported()) {
        return CancelablePromise.resolve(false);
    }
    return callNative("clearItem", (module, callback) => {
        if (module.clearItem === undefined) {
            throw new Error("clearItem is missing");
        }
        module.clearItem({ owner }, callback);
    });
}
